import { makeAutoObservable, observable, runInAction } from "mobx"
import { isSuccess, StoreResults, withLoading } from "../../../stores/stores-helper"
import { WSPermissions, WSResetUserPassword, WSRoles, WSUser } from "features/users/models/users-models"
import {
    addUserAPI,
    assignUserToCustomerAPIAsync,
    assignUserToSiteAPIAsync,
    deleteUserAPI,
    deleteUserPermissionAPIAsync,
    getAllUsersAPI,
    getUserDetailsAPI,
    resetUserApiAsync,
    updateUserAPI,
    updateUserRolesAPIAsync,
} from "features/users/services/users-services"

class UsersStore {
    userMap: Record<string, WSUser> = observable.object({})
    usersLoading: boolean = false
    userDetailsLoading: boolean = false
    userLoading: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    get users() {
        return Object.values(this.userMap)
    }

    loadAllUsersAsync = async (): Promise<WSUser[]> => {
        if (this.usersLoading) return Object.values(this.userMap)

        let users: WSUser[] = []

        await withLoading(
            val => (this.usersLoading = val),
            async () => {
                const data = await getAllUsersAPI()
                if (!Array.isArray(data)) {
                    console.error("Expected array from getAllUsersAPI, got:", data)
                    return
                }

                users = data
                runInAction(() => {
                    for (const user of data) {
                        this.userMap[user.id] = observable.object(user)
                    }
                })
            },
        )

        return users
    }

    loadUserAsync = async (userId: string): Promise<WSUser | null> => {
        if (this.userDetailsLoading) return null

        let user: WSUser | null = null

        await withLoading(
            val => (this.userDetailsLoading = val),
            async () => {
                const fetched = await getUserDetailsAPI(userId)
                if (!fetched) {
                    console.error("Expected user from getUserDetailsAPI, got:", fetched)
                    return
                }

                user = fetched

                runInAction(() => {
                    this.userMap[fetched.id] = observable.object(fetched)
                })
            },
        )

        return user
    }

    updateUserRoles = async (userId: string, updatedRoles: WSRoles[]): Promise<StoreResults> => {
        return await withLoading(
            val => (this.userLoading = val),
            async () => {
                const response = await updateUserRolesAPIAsync(userId, updatedRoles)
                console.log(response)
                if (isSuccess(response.statusCode)) {
                    runInAction(() => {
                        const existingUser = this.userMap[userId]
                        if (existingUser) {
                            this.userMap[userId] = {
                                ...existingUser,
                                roles: [...updatedRoles],
                            }
                        }
                    })
                    return { passed: true, message: "" }
                }

                return {
                    passed: false,
                    message: "Failed to update roles. Please check logs.",
                }
            },
        )
    }

    resetPasswordAsync = async (data: WSResetUserPassword): Promise<StoreResults> => {
        return await withLoading(
            val => (this.userLoading = val),
            async () => {
                const response = await resetUserApiAsync(data)

                if (isSuccess(response.statusCode)) {
                    return { passed: true, message: "" }
                }

                return {
                    passed: false,
                    message: "Password reset failed. Please check logs.",
                }
            },
        )
    }

    private assignPermissions = async (apiCall: () => Promise<any>, userId: string): Promise<StoreResults> => {
        return await withLoading(
            val => (this.userLoading = val),
            async () => {
                const response = await apiCall()
                if (isSuccess(response.statusCode)) {
                    runInAction(() => {
                        const existingUser = this.userMap[userId]
                        if (existingUser) {
                            existingUser.permissions = response.result as WSPermissions[]
                        }
                    })
                    return { passed: true, message: "" }
                }
                return { passed: false, message: "Failed to assign permission." }
            },
        )
    }

    assignUserToCustomer = (userId: string, customerId: string) =>
        this.assignPermissions(() => assignUserToCustomerAPIAsync(userId, customerId), userId)

    assignUserToSite = (userId: string, siteId: string) => this.assignPermissions(() => assignUserToSiteAPIAsync(userId, siteId), userId)

    removeUserPermission = async (userId: string, permissionId: string): Promise<StoreResults> => {
        return await withLoading(
            val => (this.userLoading = val),
            async () => {
                const response = await deleteUserPermissionAPIAsync(userId, permissionId)
                if (isSuccess(response.statusCode)) {
                    runInAction(() => {
                        const user = this.userMap[userId]
                        if (user) {
                            user.permissions = user.permissions?.filter(p => p.id !== permissionId) ?? []
                        }
                    })
                    return { passed: true, message: "" }
                }
                return { passed: false, message: "Failed to remove permission." }
            },
        )
    }

    createEntity = async (entity: Partial<WSUser>): Promise<StoreResults> => {
        return await withLoading(
            val => (this.userLoading = val),
            async () => {
                const response = await addUserAPI(entity)
                if (isSuccess(response.statusCode)) {
                    const createdEntity = response.result as WSUser
                    const entityId = createdEntity?.id ?? ""
                    runInAction(() => {
                        this.userMap[entityId] = observable.object(createdEntity)
                    })
                    return { passed: true, message: "", entityId }
                } else if (response.statusCode === 409) {
                    return {
                        passed: false,
                        message: "Conflict-409: an entity with the same name already exists.",
                        entityId: "",
                    }
                }
                return { passed: false, message: "An error has occurred, please check the logs", entityId: "" }
            },
        )
    }

    editEntity = async (entity: Partial<WSUser>): Promise<StoreResults> => {
        return await withLoading(
            val => (this.userLoading = val),
            async () => {
                const response = await updateUserAPI(entity)
                if (isSuccess(response.statusCode)) {
                    const entityId = (entity as WSUser)?.id ?? ""
                    runInAction(() => {
                        const existingUser = this.userMap[entityId]
                        if (existingUser) {
                            this.userMap[entityId] = {
                                ...existingUser,
                                ...entity,
                            }
                        }
                    })
                    return { passed: true, message: "", entityId }
                }
                return { passed: false, message: "An error has occurred, please check the logs", entityId: "" }
            },
        )
    }

    deleteEntity = async (entityId: string): Promise<StoreResults> => {
        return await withLoading(
            val => (this.usersLoading = val),
            async () => {
                const response = await deleteUserAPI(entityId)
                if (isSuccess(response.statusCode)) {
                    runInAction(() => {
                        delete this.userMap[entityId]
                    })
                    return { passed: true, message: "" }
                }
                return { passed: false, message: "An error has occurred, please check the logs" }
            },
        )
    }
}
const usersStore = new UsersStore()
export default usersStore
